import { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from './contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { MobileMenu } from './components/MobileMenu';
import { MobileHeader } from './components/MobileHeader';
import { ManuscriptEditor } from './components/editor/ManuscriptEditor';
const ConstellationView = lazy(() => import('./components/ConstellationView').then((m) => ({ default: m.ConstellationView })));
const CorkBoardView = lazy(() => import('./components/CorkBoardView').then((m) => ({ default: m.CorkBoardView })));
const Chronoscope = lazy(() => import('./components/timeline/Chronoscope').then((m) => ({ default: m.Chronoscope })));
import { CompostHeap } from './components/CompostHeap';
import { BubblePanel } from './components/bubble/BubblePanel';
import { Dashboard } from './components/Dashboard';
const StudioView = lazy(() => import('./components/studio/StudioView').then((m) => ({ default: m.StudioView })));
const ChallengesView = lazy(() => import('./components/ChallengesView').then((m) => ({ default: m.ChallengesView })));
import { DailyPrompt } from './components/DailyPrompt';
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })));
import Onboarding from './components/Onboarding';
import { AuthorDictionary } from './components/editor/AuthorDictionary';
import { SettingsPanel } from './components/settings/SettingsPanel';
import { WritingStatsView } from './components/stats/WritingStatsView';
import { useAppStore } from './lib/store';
import { useCloudSync, type SyncConflict } from './hooks/use-cloud-sync';
import { SyncConflictModal, SyncStatus } from './components/SyncModals';
import {
  EditorBoundary,
  ConstellationBoundary,
  TimelineBoundary,
  CompostBoundary,
  SidebarBoundary,
} from './components/ErrorBoundary';

import { cn } from './lib/utils';
import { useMobile } from './hooks/use-mobile';
import { FloatingNav } from './components/navigation/FloatingNav';
import { ViewBreadcrumbs } from './components/navigation/ViewBreadcrumbs';

function ViewLoader() {
  return (
    <div className="h-full flex items-center justify-center font-serif text-zinc-500">
      <div className="animate-pulse">Loading view...</div>
    </div>
  );
}

function App() {
  const {
    viewMode,
    showBubblePanel,
    activeNovelId,
    novels,
    theme,
    themeProfile,
    showSettings,
    setShowSettings,
    applyThemeToCSS,
    sidebarMode,
    setViewMode,
    loadNovelsFromCloud,
    isSyncing,
    hasCompletedOnboarding,
  } = useAppStore();

  // Conflict + sync state for cloud sync
  const [activeConflict, setActiveConflict] = useState<SyncConflict | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error' | 'offline'>('idle');

  // Initialize the Cloud Sync with conflict + status callbacks
  useCloudSync({
    onConflict: (conflict) => setActiveConflict(conflict),
    onSyncStatusChange: (status) => setSyncStatus(status),
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isMobile } = useMobile();

  // Cloudflare Auth State
  const { user, isLoading: isAuthLoading } = useAuth();

  useEffect(() => {
    if (user) {
      // Load cloud state on login
      loadNovelsFromCloud();
    }
  }, [user, loadNovelsFromCloud]);

  const activeNovel = novels.find(n => n.id === activeNovelId);
  const activeChapter = activeNovel?.chapters.find(ch => ch.id === activeNovel.activeChapterId);

  // Apply theme CSS variables
  useEffect(() => {
    applyThemeToCSS();
  }, [themeProfile, applyThemeToCSS]);

  // Apply dark/light theme
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else if (theme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    }
  }, [theme]);

  // Check browser compatibility
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

    if (isSafari || isFirefox || isIOS) {
      console.warn('Browser might lack full support for advanced features. Ensure polyfills are active.');
    }
  }, []);

  // Auth Guard
  if (isAuthLoading || isSyncing) {
    return (
      <div className="min-h-screen app-background flex items-center justify-center font-serif text-zinc-500">
        <div className="animate-pulse">Opening your Nexus...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Onboarding Flow
  if (user && !hasCompletedOnboarding) {
    // If they magically survived onboarding (or existed before it) and have novels, just mark it done so they aren't surprised.
    // Otherwise, show them the onboarding.
    if (novels.length > 0) {
      useAppStore.getState().completeOnboarding();
    } else {
      return <Onboarding />;
    }
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div
        className="h-screen overflow-hidden"
        style={{
          backgroundColor: themeProfile.background,
          color: themeProfile.text,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        {/* Mobile Header */}
        <MobileHeader onMenuClick={() => setMobileMenuOpen(true)} />

        {/* Mobile Menu */}
        <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

        {/* Settings Panel */}
        <SettingsPanel isOpen={showSettings} onClose={() => setShowSettings(false)} />

        {/* Main Content */}
        <div className="h-full pt-14 overflow-hidden">
          <Suspense fallback={<ViewLoader />}>
            {viewMode === 'dashboard' && <Dashboard onSelectNovel={() => { }} />}

            {viewMode === 'manuscript' && activeChapter && (
              <ManuscriptEditor chapter={activeChapter} />
            )}

            {viewMode === 'constellation' && <ConstellationView />}
            {viewMode === 'corkboard' && <CorkBoardView />}
            {viewMode === 'timeline' && <Chronoscope />}
            {viewMode === 'compost' && <CompostHeap />}
            {viewMode === 'dictionary' && <AuthorDictionary />}
            {viewMode === 'stats' && <WritingStatsView />}
            {viewMode === 'studio' && <StudioView />}
            {viewMode === 'challenges' && <ChallengesView />}
            {viewMode === 'admin' && <AdminDashboard />}
          </Suspense>

          {viewMode === 'manuscript' && !activeChapter && activeNovelId && (
            <div className="flex items-center justify-center h-full" style={{ color: themeProfile.text, opacity: 0.7 }}>
              <div className="text-center px-4">
                <p className="text-lg mb-2">No chapters yet</p>
                <p className="text-sm mb-4">Create a chapter to start writing</p>
                <button
                  onClick={() => {
                    const { createChapter } = useAppStore.getState();
                    createChapter();
                  }}
                  className="px-6 py-3 rounded-xl font-medium transition-all hover:scale-105"
                  style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                >
                  Create Chapter
                </button>
              </div>
            </div>
          )}

          {!activeNovelId && viewMode !== 'dashboard' && (
            <div className="flex items-center justify-center h-full" style={{ color: themeProfile.text, opacity: 0.7 }}>
              <div className="text-center px-4">
                <p className="text-lg mb-2">No novel selected</p>
                <p className="text-sm mb-4">Select or create a novel to continue</p>
                <button
                  onClick={() => setViewMode('dashboard')}
                  className="px-6 py-3 rounded-xl font-medium transition-all hover:scale-105"
                  style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{
        backgroundColor: themeProfile.background,
        color: themeProfile.text,
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >

      {/* Sidebar */}
      {viewMode !== 'dashboard' && (
        <SidebarBoundary>
          <Sidebar />
        </SidebarBoundary>
      )}

      {/* Main Content */}
      <div
        className={cn(
          'flex-1 flex transition-all duration-300 overflow-hidden',
          viewMode === 'dashboard' ? 'ml-0' : sidebarMode === 'pinned' ? 'ml-56' : 'ml-16'
        )}
      >
        {/* Primary View */}
        <div className="flex-1 relative overflow-hidden flex flex-col">
          {viewMode !== 'dashboard' && <ViewBreadcrumbs />}
          <div className="flex-1 relative overflow-hidden">
            <Suspense fallback={<ViewLoader />}>
              {viewMode === 'dashboard' && <Dashboard onSelectNovel={() => { }} />}

              {viewMode === 'manuscript' && activeChapter && (
                <EditorBoundary>
                  <ManuscriptEditor chapter={activeChapter} />
                </EditorBoundary>
              )}

              {viewMode === 'constellation' && (
                <ConstellationBoundary><ConstellationView /></ConstellationBoundary>
              )}
              {viewMode === 'corkboard' && <CorkBoardView />}
              {viewMode === 'timeline' && (
                <TimelineBoundary><Chronoscope /></TimelineBoundary>
              )}
              {viewMode === 'compost' && (
                <CompostBoundary><CompostHeap /></CompostBoundary>
              )}
              {viewMode === 'dictionary' && <AuthorDictionary />}
              {viewMode === 'stats' && <WritingStatsView />}
              {viewMode === 'studio' && <StudioView />}
              {viewMode === 'admin' && <AdminDashboard />}
            </Suspense>

            {viewMode === 'manuscript' && !activeChapter && activeNovelId && (
              <div className="flex items-center justify-center h-full" style={{ color: themeProfile.text, opacity: 0.7 }}>
                <div className="text-center">
                  <p className="text-lg mb-2">No chapters yet</p>
                  <p className="text-sm mb-4">Create a chapter to start writing</p>
                  <button
                    onClick={() => {
                      const { createChapter } = useAppStore.getState();
                      createChapter();
                    }}
                    className="px-6 py-3 rounded-xl font-medium transition-all hover:scale-105"
                    style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                  >
                    Create Chapter
                  </button>
                </div>
              </div>
            )}

            {!activeNovelId && viewMode !== 'dashboard' && (
              <div className="flex items-center justify-center h-full" style={{ color: themeProfile.text, opacity: 0.7 }}>
                <div className="text-center">
                  <p className="text-lg mb-2">No novel selected</p>
                  <p className="text-sm mb-4">Select or create a novel to continue</p>
                  <button
                    onClick={() => setViewMode('dashboard')}
                    className="px-6 py-3 rounded-xl font-medium transition-all hover:scale-105"
                    style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bubble Panel - Hide in Constellation view as it has its own internal panel */}
        {showBubblePanel && viewMode !== 'dashboard' && viewMode !== 'constellation' && (
          <div
            className="w-72 flex-shrink-0 overflow-hidden"
            style={{
              borderLeft: `1px solid ${themeProfile.border}`,
              backgroundColor: themeProfile.background,
            }}
          >
            <BubblePanel />
          </div>
        )}
      </div>

      <SettingsPanel isOpen={showSettings} onClose={() => setShowSettings(false)} />

      {/* Daily Prompt */}
      <DailyPrompt />

      {/* Sync conflict resolution modal */}
      {activeConflict && (
        <SyncConflictModal
          conflict={activeConflict}
          onDismiss={() => setActiveConflict(null)}
        />
      )}

      {/* Sync status (rendered inline — can be moved to toolbar) */}
      <div style={{ position: 'fixed', bottom: 8, left: 8, zIndex: 50, pointerEvents: 'none' }}>
        <SyncStatus status={syncStatus} />
      </div>

      {/* Floating Global Navigation - Hidden in focused writing/view modes */}
      {(viewMode === 'dashboard' || isMobile) && <FloatingNav />}
    </div>
  );
}

export default App;
